import { useRef, type MouseEvent } from 'react';
import { useReducedMotion } from 'motion/react';
import { useStore } from '../../lib/store';
import { Icon } from '../icons/Icon';
import { BrandOwl } from '../primitives';
import { useIsDark } from '../theme/themeMode';
import { OVERLAYS, TABS, type Destination, type OverlayId, type TabId } from './destinations';
import { focusLayerStart, useNavigationFocusHandOff } from './focus';
import { nav, shellNavStore } from './nav';
import { scrollPaneToTop } from './scrollToTop';
import styles from './DesktopSidebar.module.css';

type SidebarId = TabId | OverlayId;

function tab(id: TabId): Destination<TabId> {
  return TABS.find((t) => t.id === id) as Destination<TabId>;
}

function overlay(id: OverlayId): Destination<OverlayId> {
  return OVERLAYS.find((o) => o.id === id) as Destination<OverlayId>;
}

/**
 * The Menu tab's destinations, minus Menu itself: on a computer every
 * destination fits in the sidebar, so the Menu tab is not needed.
 */
const PRIMARY: readonly Destination<SidebarId>[] = [tab('home'), tab('library'), tab('downloads'), overlay('reading-list'), overlay('statistics')];
const SECONDARY: readonly Destination<SidebarId>[] = [overlay('connections'), tab('settings')];

function isOverlay(id: SidebarId): id is OverlayId {
  return OVERLAYS.some((o) => o.id === id);
}

function isModifiedClick(event: MouseEvent): boolean {
  return event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
}

/** The shown tab pane or overlay, when nothing is layered above it. */
function shownScreen(id: SidebarId): HTMLElement | null {
  const selector = isOverlay(id) ? `[data-overlay="${id}"]` : `[data-tab="${id}"]`;
  return document.querySelector<HTMLElement>(selector);
}

/** The screen on top: the last exposed main landmark (covered layers and hidden tab panes are inert). */
function topMainLandmark(): HTMLElement | null {
  const mains = Array.from(document.querySelectorAll<HTMLElement>('[role="main"], main')).filter(
    (main) => main.closest('[inert], [aria-hidden="true"]') === null,
  );
  return mains[mains.length - 1] ?? null;
}

/** "Skip to content": keyboard focus moves to the shown screen's title, else to the screen itself. */
function skipToContent(event: MouseEvent<HTMLAnchorElement>) {
  event.preventDefault();
  const main = topMainLandmark();
  if (main) focusLayerStart(main);
}

/**
 * Desktop layout: the sidebar that replaces the bottom nav bar. The brand,
 * then Home, Library, Downloads, Reading List and Statistics, and Connections
 * and Settings at the bottom. Links carry the tab and overlay labels as their
 * names; the current one has `aria-current="page"` and stays marked while a
 * page opened from it is on top. Clicking the current destination with
 * nothing above it scrolls it back to the top (as a nav-bar re-tap does);
 * with a page on top it returns to the destination. A "Skip to content"
 * link, drawn only while focused, comes first in the tab order.
 */
export function DesktopSidebar() {
  const activeTab = useStore(shellNavStore, (s) => s.activeTab);
  const openOverlay = useStore(shellNavStore, (s) => s.layer.overlay);
  const depth = useStore(shellNavStore, (s) => s.layer.depth);
  const reduced = useReducedMotion() ?? false;
  const dark = useIsDark();
  const current: SidebarId | null = openOverlay ?? (activeTab === 'menu' ? null : activeTab);
  const navRef = useRef<HTMLElement>(null);
  useNavigationFocusHandOff(navRef);

  const select = (id: SidebarId) => {
    if (id === current && depth === 0) {
      const screen = shownScreen(id);
      if (screen) scrollPaneToTop(screen, reduced);
      return;
    }
    if (isOverlay(id)) nav.overlay(id);
    else nav.tab(id);
  };

  const link = (destination: Destination<SidebarId>) => {
    const selected = destination.id === current;
    return (
      <li key={destination.id}>
        <a
          href={destination.path}
          className={styles.link}
          aria-current={selected ? 'page' : undefined}
          onClick={(event) => {
            if (isModifiedClick(event)) return;
            event.preventDefault();
            select(destination.id);
          }}
        >
          <Icon name={selected ? destination.selectedIcon : destination.icon} size={20} className={styles.icon} />
          <span className={styles.label}>{destination.label}</span>
        </a>
      </li>
    );
  };

  return (
    <>
      <a href="#content" className={styles.skip} onClick={skipToContent}>
        Skip to content
      </a>
      <nav ref={navRef} className={styles.sidebar} aria-label="Main">
        <div className={styles.brand}>
          {dark ? <BrandOwl variant="mono" height={30} className={styles.owlMono} /> : <BrandOwl height={30} />}
          <span className={styles.wordmark}>Plume</span>
        </div>
        <ul className={styles.list}>{PRIMARY.map(link)}</ul>
        <ul className={[styles.list, styles.bottom].join(' ')}>{SECONDARY.map(link)}</ul>
      </nav>
    </>
  );
}
